import * as React from "react";
import { Button, CardTabs, useModal } from "@racoon/ui";
import { Namespace, useT, type TranslationKey } from "@racoon/i18n";
import {
  ONBOARDING_STEPS,
  isStepUnlocked,
  nextStep,
  type OnboardingProgress,
  type OnboardingStep,
} from "../model/types";
import { CustomerStep } from "../components/customer.step";
import { ContractStep } from "../components/contract.step";
import { LocationStep } from "../components/location.step";
import { LocationListStep } from "../components/container-groups.step";

export type CloseGuard = { current: () => boolean };

const STEP_LABEL_KEY = {
  customer: "onboarding.step.customer",
  contract: "onboarding.step.contract",
  location: "onboarding.step.location",
  containerGroups: "onboarding.step.containerGroups",
  schedule: "onboarding.step.schedule",
} as const satisfies Record<OnboardingStep, TranslationKey<Namespace.Customers>>;

type Props = {
  providerId?: number;
  /** Set by the content so the shell's dismissal paths confirm while a form holds unsaved input. */
  closeGuardRef: CloseGuard;
  onClose: () => void;
  onFinished?: () => void;
};

export const CustomerOnboardingContent: React.FC<Props> = ({
  providerId,
  closeGuardRef,
  onClose,
  onFinished,
}) => {
  const t = useT();
  const { openModal, closeModal } = useModal();

  const [activeStep, setActiveStep] = React.useState<OnboardingStep>("customer");
  const [progress, setProgress] = React.useState<OnboardingProgress>({});
  const [customerName, setCustomerName] = React.useState("");

  // Steps stay mounted, so any of them may hold a draft; read inside the guard, which the provider holds across renders.
  const dirtyRef = React.useRef<Partial<Record<OnboardingStep, boolean>>>({});
  const markDirty = (step: OnboardingStep) => (dirty: boolean) => {
    dirtyRef.current[step] = dirty;
  };

  const close = React.useCallback(() => {
    // Nothing to roll back: steps write through as they go.
    onFinished?.();
    onClose();
  }, [onClose, onFinished]);

  const requestClose = React.useCallback(() => {
    if (!Object.values(dirtyRef.current).some(Boolean)) {
      close();
      return true;
    }

    // Same provider as the wizard, so the confirm stacks on top; its id exists only after openModal returns.
    const confirmId = { current: "" };
    confirmId.current = openModal({
      size: "md",
      showClose: false,
      // Body, not `description`: an empty body would stack the header and footer rules.
      title: (
        <span className="text-base font-semibold leading-snug text-text-body">
          {t(Namespace.Customers, "onboarding.confirmClose.title")}
        </span>
      ),
      body: (
        <p className="text-sm leading-snug text-icon-secondary">
          {t(Namespace.Customers, "onboarding.confirmClose.description")}
        </p>
      ),
      footer: (
        <>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => closeModal(confirmId.current)}
          >
            {t(Namespace.Customers, "onboarding.confirmClose.cancel")}
          </Button>
          <Button
            type="button"
            size="sm"
            variant="solidRed"
            onClick={() => {
              closeModal(confirmId.current);
              close();
            }}
          >
            {t(Namespace.Customers, "onboarding.confirmClose.confirm")}
          </Button>
        </>
      ),
    });

    return false;
  }, [close, closeModal, openModal, t]);

  React.useEffect(() => {
    closeGuardRef.current = requestClose;
  }, [closeGuardRef, requestClose]);

  /** Saving a step unlocks the next one and moves there. */
  const advance = (from: OnboardingStep, patch: Partial<OnboardingProgress>) => {
    setProgress((prev) => ({ ...prev, ...patch }));
    const next = nextStep(from);
    if (next) setActiveStep(next);
  };

  // A step is locked until the step before it is saved.
  const tabItems = ONBOARDING_STEPS.map((step) => ({
    key: step,
    label: t(Namespace.Customers, STEP_LABEL_KEY[step]),
    disabled: !isStepUnlocked(step, progress),
  }));

  // Title and cross come from the shell (see the hook). Panel scroll is off: each StepShell scrolls itself.
  return (
    <CardTabs
      items={tabItems}
      value={activeStep}
      onValueChange={setActiveStep}
      idPrefix="customer-onboarding"
      panelClassName="!overflow-hidden"
    >
      {/* Steps stay mounted (hidden) so half-filled forms survive; list steps mount once unlocked. */}
      <StepPane active={activeStep === "customer"}>
        <CustomerStep
          providerId={providerId}
          organizationId={progress.organizationId}
          onDirtyChange={markDirty("customer")}
          onCreated={(organizationId, name) => {
            setCustomerName(name);
            advance("customer", { organizationId, contractId: undefined, locationId: undefined });
          }}
          onPickedExisting={(organization) => {
            setCustomerName(organization.name);
            advance("customer", {
              organizationId: BigInt(organization.id),
              contractId: undefined,
              locationId: undefined,
            });
          }}
        />
      </StepPane>

      <StepPane active={activeStep === "contract"}>
        <ContractStep
          providerId={providerId}
          organizationId={progress.organizationId}
          customerName={customerName}
          contractId={progress.contractId}
          onDirtyChange={markDirty("contract")}
          onCreated={(contractId) => advance("contract", { contractId, locationId: undefined })}
          onPickedExisting={(contractId) =>
            advance("contract", { contractId, locationId: undefined })
          }
        />
      </StepPane>

      <StepPane active={activeStep === "location"}>
        <LocationStep
          providerId={providerId}
          organizationId={progress.organizationId}
          contractId={progress.contractId}
          locationId={progress.locationId}
          onDirtyChange={markDirty("location")}
          onSaved={(locationId) => advance("location", { locationId })}
        />
      </StepPane>

      {/* Reachable once step 3 has a site; steps 4 and 5 preselect it. */}
      {isStepUnlocked("containerGroups", progress) && (
        <>
          <StepPane active={activeStep === "containerGroups"}>
            <LocationListStep
              providerId={providerId}
              organizationId={progress.organizationId}
              contractId={progress.contractId}
              locationId={progress.locationId}
              variant="containerGroups"
            />
          </StepPane>

          <StepPane active={activeStep === "schedule"}>
            <LocationListStep
              providerId={providerId}
              organizationId={progress.organizationId}
              contractId={progress.contractId}
              locationId={progress.locationId}
              variant="schedule"
            />
          </StepPane>
        </>
      )}
    </CardTabs>
  );
};

/** Hides a step without unmounting it, keeping form state and sub-modals. */
const StepPane: React.FC<{ active: boolean; children: React.ReactNode }> = ({
  active,
  children,
}) => (
  <div className={active ? "flex min-h-0 flex-1 flex-col" : "hidden"} aria-hidden={!active}>
    {children}
  </div>
);
